import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Camera, Mic, Pause, Play, Send, Square, Type, X } from "lucide-react";
import { toast } from "sonner";
import { useCaptureRecorder } from "@/hooks/useCaptureRecorder";
import { enqueueCapture } from "@/lib/captureQueue";
import { CaptureRow, useCaptures } from "@/components/conecta/CaptureRow";

const fmt = (ms) => { const s = Math.floor(ms / 1000); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };
const MODES = [{ id: "voz", label: "Voz", icon: Mic }, { id: "foto", label: "Foto", icon: Camera }, { id: "texto", label: "Texto", icon: Type }];

const HOLD_MS = 450;

const VoiceMode = ({ auto }) => {
  const r = useCaptureRecorder();
  const started = useRef(false);
  const press = useRef(null);
  const [holding, setHolding] = useState(false);
  useEffect(() => { if (auto && !started.current) { started.current = true; r.start(); } }, [auto, r]);
  const active = r.state !== "idle";
  const send = async () => {
    const out = await r.stop();
    if (!out || out.blob.size < 1000) return toast.error("Grabación vacía o muy corta.");
    await enqueueCapture({ kind: "audio", blob: out.blob, filename: `nota.${out.ext}` });
    toast.success("Nota guardada en el teléfono. Subiendo…");
  };
  const onDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    press.current = { at: Date.now(), startedHere: !active };
    if (!active) { r.start(); setTimeout(() => press.current?.startedHere && setHolding(true), HOLD_MS); }
  };
  const onUp = () => {
    const p = press.current;
    press.current = null;
    setHolding(false);
    if (!p) return;
    if (!p.startedHere || Date.now() - p.at >= HOLD_MS) send();
  };
  const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); active ? send() : r.start(); } };
  return (
    <div className="flex flex-col items-center gap-6 py-6" data-testid="voice-mode">
      <div data-testid="grabar-timer" className={`font-mono text-6xl font-black tabular-nums ${r.state === "recording" ? "text-red-500" : ""}`}>{fmt(r.elapsed)}</div>
      <div className="flex items-center gap-5">
        <button data-testid="grabar-cancel-btn" onClick={r.cancel} aria-label="Cancelar" className={`w-14 h-14 rounded-full c-card flex items-center justify-center ${active && !holding ? "" : "invisible"}`}><X className="w-6 h-6" /></button>
        <button data-testid={active ? "grabar-stop-btn" : "grabar-start-btn"} onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={onUp} onKeyDown={onKey}
          onContextMenu={(e) => e.preventDefault()} aria-label={active ? "Detener y enviar" : "Grabar (mantén presionado y suelta para enviar)"}
          style={{ touchAction: "none", WebkitTouchCallout: "none" }}
          className={`relative w-28 h-28 rounded-full bg-[#dc2626] text-white flex items-center justify-center select-none transition-transform shadow-xl shadow-red-900/30 ${holding ? "scale-110" : "active:scale-95"} ${active ? "rec-ring" : ""}`}>
          {active ? <Square className="w-10 h-10 fill-white" /> : <Mic className="w-12 h-12" />}
        </button>
        <button data-testid={r.state === "paused" ? "grabar-resume-btn" : "grabar-pause-btn"} onClick={r.state === "paused" ? r.resume : r.pause} aria-label="Pausa" className={`w-14 h-14 rounded-full c-card flex items-center justify-center ${active && !holding ? "" : "invisible"}`}>
          {r.state === "paused" ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
        </button>
      </div>
      <p data-testid="grabar-status" className="text-sm c-text-2 text-center">
        {holding ? "Grabando · suelta para enviar" : r.state === "recording" ? "Grabando · toca el cuadro para enviar" : r.state === "paused" ? "En pausa" : "Toca para grabar, o mantén presionado y suelta para enviar."}
      </p>
      {r.error && <p data-testid="grabar-error" className="text-sm text-red-500 text-center">{r.error}</p>}
    </div>
  );
};

const PhotoMode = () => {
  const [file, setFile] = useState(null);
  const url = file ? URL.createObjectURL(file) : null;
  const send = async () => {
    await enqueueCapture({ kind: "photo", blob: file, filename: file.name || "foto.jpg" });
    setFile(null);
    toast.success("Foto guardada. Subiendo…");
  };
  return (
    <div className="space-y-3 py-4" data-testid="photo-mode">
      <label data-testid="photo-pick-label" className="c-card flex flex-col items-center justify-center gap-2 py-10 cursor-pointer">
        <Camera className="w-10 h-10 c-accent" />
        <span className="text-sm c-text-2">Tarjeta, recibo o libreta</span>
        <input data-testid="photo-file-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      {url && <img src={url} alt="Vista previa" className="w-full max-h-72 object-contain rounded-xl c-card" />}
      <button data-testid="photo-send-btn" disabled={!file} onClick={send} className="c-btn-primary w-full py-3.5 flex items-center justify-center gap-2"><Send className="w-4 h-4" /> Enviar foto</button>
    </div>
  );
};

const TextMode = () => {
  const [text, setText] = useState("");
  const send = async () => {
    await enqueueCapture({ kind: "text", text: text.trim() });
    setText("");
    toast.success("Nota guardada. Subiendo…");
  };
  return (
    <div className="space-y-3 py-4" data-testid="text-mode">
      <textarea data-testid="quick-text-input" className="c-input min-h-[140px]" placeholder="Hablé con… necesita… quedamos en…" value={text} onChange={(e) => setText(e.target.value)} />
      <button data-testid="quick-text-send-btn" disabled={!text.trim()} onClick={send} className="c-btn-primary w-full py-3.5 flex items-center justify-center gap-2"><Send className="w-4 h-4" /> Enviar nota</button>
    </div>
  );
};

export default function GrabarPage() {
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState("voz");
  const auto = params.get("auto") === "1";
  useEffect(() => { if (auto) setParams({}, { replace: true }); }, [auto, setParams]);
  const recent = useCaptures({ mine: true, limit: 5 });
  return (
    <div data-testid="grabar-page" className="space-y-3">
      <div className="flex gap-1 p-1 rounded-xl bg-[var(--c-elev)]">
        {MODES.map(({ id, label, icon: Icon }) => (
          <button key={id} data-testid={`grabar-mode-${id}`} onClick={() => setMode(id)}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${mode === id ? "bg-[var(--c-card)] shadow-sm c-accent" : "c-muted"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      {mode === "voz" && <VoiceMode auto={auto} />}
      {mode === "foto" && <PhotoMode />}
      {mode === "texto" && <TextMode />}
      <div className="space-y-2" data-testid="recent-captures">
        <h2 className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted">Mis últimas capturas</h2>
        {recent?.length === 0 && <p className="text-sm c-muted">Aún no hay capturas.</p>}
        {recent?.map((c) => <CaptureRow key={c.id} c={c} />)}
      </div>
    </div>
  );
}
